import React, { useState, useEffect } from "react";
import { Row, Col, Button, Spinner } from "reactstrap";
import { isEmpty } from "lodash";
import moment from "moment";

import StatCard from "../../components/SATI/StatCard";
import BarChart from "../../components/SATI/BarChart";
import LineChart from "../../components/SATI/LineChart";
import AreaChart from "../../components/SATI/AreaChart";
import DonutChart from "../../components/SATI/DonutChart";
import AlertModal from "../../components/SATI/AlertModal";
import DateRangePicker from "../../components/SATI/DateRangePicker";
import SummaryMessages from "../../components/SATI/SummaryMessages";
import descriptions from "../../helpers/chartsDescriptions";

import {
  getUserData,
  getUserWeekData,
  getUserWeekTemperature,
  getUserWeekOxygen,
  getUserTemperature,
  getUserOxygen,
  getUserSummayMonth,
  getUserWeekSummary,
  getUserIndividualSleep,
  getUserIndividualSleepWeek,
} from "../../helpers/satiBack";
import {
  stressRanges,
  cardiovascularHealthRanges,
  sleepScoreRanges,
} from "../../helpers/statCardRanges";
import {
  HEART_ICON_STRING,
  SLEEP_ICON_STRING,
  SMILE_ICON_STRING,
} from "../../assets/images/SATI/charts/svg-strings";

import { getRefValue, getCategoryCounts } from "../../helpers/dashHelper";
import useReportFilter from "../../hooks/useReportFilter";

import { ReactComponent as CaloriesIcon } from "../../assets/images/SATI/calories.svg";
import { ReactComponent as DistanceIcon } from "../../assets/images/SATI/km.svg";
import { ReactComponent as StepsIcon } from "../../assets/images/SATI/steps.svg";
import { ReactComponent as OxigenIcon } from "../../assets/images/SATI/oxigen.svg";
import { ReactComponent as HeartRateIcon } from "../../assets/images/SATI/heart-rate.svg";
import { ReactComponent as CalendarIcon } from "../../assets/images/SATI/calendar-icon.svg";
import { ReactComponent as HeartLogoIcon } from "../../assets/images/SATI/ibicare-heart.svg";
import { ReactComponent as LeftArrowIcon } from "../../assets/images/SATI/left-arrow-icon.svg";

import "chartist/dist/scss/chartist.scss";

const UserDashboard = ({ user }) => {
  const [tab, setTab] = useState("general");
  const [summaryData, setSummaryData] = useState([]);
  const [temperatureData, setTemperatureData] = useState([]);
  const [oxygenData, setOxygenData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(0);

  const getChartData = (loading, start, end) => {
    setLoadingBtn(loading);

    const chartPromise = getUserData(start, end)
      .then((data) => {
        setChartData(data);
      })
      .catch((err) => {
        console.error(err);
      });

    const summaryPromise = getUserSummayMonth()
      .then((data) => {
        setSummaryData(data[0]);
      })
      .catch((err) => {
        console.error(err);
      });

    const temperaturePromise = getUserTemperature(start, end)
      .then((data) => {
        setTemperatureData(data);
      })
      .catch((err) => {
        console.log("Error:", err);
      });

    const oxygenPromise = getUserOxygen(start, end)
      .then((data) => {
        setOxygenData(data);
      })
      .catch((err) => {
        console.log("Error:", err);
      });

    const sleepPromise = getUserIndividualSleep(start, end, user.id)
      .then((data) => {
        setSleepData(
          data.map((d) => ({
            date: d.HistoryDate,
            score: d.Valor,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
      });

    Promise.all([
      chartPromise,
      summaryPromise,
      temperaturePromise,
      oxygenPromise,
      sleepPromise,
    ]).then((_) => {
      setLoadingBtn(0);
    });
  };

  const handleWeeklyReport = () => {
    setLoadingBtn(1);

    const chartPromise = getUserWeekData()
      .then((data) => {
        setChartData(data);
      })
      .catch((err) => {
        console.error(err);
      });

    const summaryPromise = getUserWeekSummary()
      .then((data) => {
        setSummaryData(data[0]);
      })
      .catch((err) => {
        console.error(err);
      });

    const temperaturePromise = getUserWeekTemperature()
      .then((data) => {
        setTemperatureData(data);
      })
      .catch((err) => {
        console.log("Error:", err);
      });

    const oxygenPromise = getUserWeekOxygen()
      .then((data) => {
        setOxygenData(data);
      })
      .catch((err) => {
        console.log("Error:", err);
      });

    const sleepPromise = getUserIndividualSleepWeek(user.id)
      .then((data) => {
        setSleepData(
          data.map((d) => ({
            date: d.HistoryDate,
            score: d.Valor,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
      });

    Promise.all([
      chartPromise,
      summaryPromise,
      temperaturePromise,
      oxygenPromise,
      sleepPromise,
    ]).then((_) => {
      setLoadingBtn(0);
    });
  };

  const {
    range: dateRange,
    setRange: setDateRange,
    setWeekly,
    setMonthly,
    setCustom
  } = useReportFilter({
    useIsoWeek: true,
    onFilterChange: (id, start, end) => {
      if (id === 1) {
        handleWeeklyReport();
      } else {
        getChartData(id, start, end);
      }
    }
  });

  useEffect(() => {
    if (!isEmpty(user)) {
      setWeekly();
    }
  }, [user]);

  return (
    <React.Fragment>
      <div className="container-fluid px-5 page-content-inner">
        <Row className="align-items-center">
          <Col>
            <div className="page-title-box py-4">
              <h1 className="text-4xl font-bold font-display text-brand-navy tracking-tight text-left">
                Bienvenid{user.sexo === "F" ? "a" : "o"}{" "}
                {user.nombre?.split(" ")[0]}!
              </h1>
              <p className="mt-1 text-lg text-brand-gray-dark text-left">
                Éste es tu tablero de bienestar:
              </p>
            </div>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="reports-buttons-container">
            <div className="d-flex flex-direction-row">
              <Button
                onClick={setWeekly}
                color="primary"
                className="btn btn-primary waves-effect waves-light mx-1"
              >
                {loadingBtn === 1 ? (
                  <Spinner size="sm" color="light" className="mr-2" />
                ) : (
                  <span>Reporte semanal</span>
                )}
              </Button>
              <Button
                onClick={setMonthly}
                color="primary"
                className="btn btn-primary waves-effect waves-light mx-1"
              >
                {loadingBtn === 2 ? (
                  <Spinner size="sm" color="light" className="mr-2" />
                ) : (
                  <span>Reporte mensual</span>
                )}
              </Button>
            </div>

            <div className="d-flex align-items-center m-2">
              <CalendarIcon
                className="m-3"
                width={25}
                height={25}
                color="#979699"
              />

              <DateRangePicker
                onSelect={(dates) => setCustom([moment(dates[0]), moment(dates[1])])}
                startDate={dateRange[0].toDate()}
                endDate={dateRange[1].toDate()}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2 justify-content-center d-flex gap-3 p-1">
          <StatCard
            loading={loadingBtn !== 0}
            onlyCardIcon
            icon={<HeartLogoIcon width={60} height={60} />}
            onClick={() => setTab("general")}
            selected={tab === "general"}
          />
          <StatCard
            loading={loadingBtn !== 0}
            title="Pasos"
            value={summaryData?.StepsAVG}
            ranges={[]}
            percent={summaryData?.PercentSteps}
            icon={<StepsIcon width={30} height={30} />}
            onClick={() => setTab("steps")}
            selected={tab === "steps"}
          />
          <StatCard
            loading={loadingBtn !== 0}
            title="Calorías"
            value={summaryData?.CaloriesAVG}
            ranges={[]}
            percent={summaryData?.PercentCalories}
            icon={<CaloriesIcon width={30} height={30} />}
            onClick={() => setTab("calories")}
            selected={tab === "calories"}
          />
          <StatCard
            loading={loadingBtn !== 0}
            title="Ritmo Cardiaco"
            value={summaryData?.CaloriesAVG}
            ranges={[]}
            percent={summaryData?.PercentCalories}
            icon={<HeartRateIcon width={30} height={30} />}
            onClick={() => setTab("hrv")}
            selected={tab === "hrv"}
          />
          <StatCard
            loading={loadingBtn !== 0}
            title="Oxígeno"
            value={summaryData?.CaloriesAVG}
            ranges={[]}
            percent={summaryData?.PercentCalories}
            icon={<OxigenIcon width={30} height={30} />}
            onClick={() => setTab("oxygen")}
            selected={tab === "oxygen"}
          />
          <StatCard
            loading={loadingBtn !== 0}
            title="Distancia"
            value={summaryData?.DistanceAVG}
            ranges={[]}
            percent={summaryData?.PercentDistance}
            icon={<DistanceIcon width={30} height={30} />}
            onClick={() => setTab("distance")}
            selected={tab === "distance"}
          />
        </Row>

        <Row className="stats-cards-container">
          <Col>
            {tab === "general" && (
              <>
                <Row>
                  <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                    <BarChart
                      title="Nivel de estrés"
                      data={chartData.map(({ date, stressAvg }) => ({
                        date,
                        value: stressAvg,
                      }))}
                      refValue={getRefValue("stress")}
                      stackedData={[]}
                      id="stressGraph"
                      popoverTitle={
                        descriptions.find(
                          (description) => description.type === "stress"
                        ).title
                      }
                      popoverBody={
                        descriptions.find(
                          (description) => description.type === "stress"
                        ).description
                      }
                    />
                  </Col>
                  <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                    <DonutChart
                      title="Nivel de estrés"
                      data={getCategoryCounts(
                        chartData.map((d) => d.stressAvg),
                        stressRanges
                      )}
                      iconString={SMILE_ICON_STRING}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                    <SummaryMessages
                      data={summaryData?.messages?.stressLevels}
                      isLoading={loadingBtn !== 0}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                    <BarChart
                      title="Calidad de sueño"
                      data={chartData.map(
                        ({
                          date,
                          sleepHoursDeep,
                          sleepHoursLight,
                          sleepHoursAwake,
                        }) => ({
                          date,
                          sleepHoursDeep,
                          sleepHoursLight,
                          sleepHoursAwake,
                        })
                      )}
                      refValue={getRefValue("sleep")}
                      stackedData={[
                        "sleepHoursDeep",
                        "sleepHoursLight",
                        "sleepHoursAwake",
                      ]}
                      id="sleepGraph"
                      popoverTitle="Prueba"
                      popoverBody="Cuerpo prueba"
                    />
                  </Col>
                  <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                    <DonutChart
                      title="Calidad de sueño"
                      data={getCategoryCounts(
                        chartData.map((d) => d.sleepHours),
                        sleepScoreRanges
                      )}
                      iconString={SLEEP_ICON_STRING}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                    <SummaryMessages
                      data={summaryData?.messages?.sleepQuality}
                      isLoading={loadingBtn !== 0}
                      onClickDetails={() => setTab("sleep-details")}
                    />
                  </Col>
                </Row>
              </>
            )}

            {tab === "sleep-details" && (
              <Row>
                <Col sm={12} md={8}>
                  <div className="d-flex justify-content-right align-items-center pb-3">
                    <Button
                      style={{
                        backgroundColor: "#1D1F3F",
                        borderRadius: "5px",
                        color: "white",
                        marginRight: 10,
                        padding: "0.25rem 0.5rem",
                      }}
                      onClick={() => setTab("general")}
                    >
                      <LeftArrowIcon width={15} height={15} />
                    </Button>
                    <span style={{ color: "#1D1F3F" }}>
                      Volver a gráfica anterior
                    </span>
                  </div>

                  <AreaChart
                    title="Sueño"
                    id="suenoGraph"
                    data={sleepData}
                    popoverTitle="Prueba"
                    popoverBody="Cuerpo prueba"
                    valueName="score"
                    sleep="a"
                  />
                </Col>
                <Col sm={12} md={4}>
                  <SummaryMessages
                    data={summaryData?.messages?.sleepQuality}
                    isLoading={loadingBtn !== 0}
                  />
                </Col>
              </Row>
            )}

            {tab === "hrv" && (
              <Row>
                <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                  <BarChart
                    title="Salud cardiovascular (HRV)"
                    data={chartData.map(({ date, hrvValueAvg }) => ({
                      date,
                      value: hrvValueAvg,
                    }))}
                    refValue={getRefValue("cardiovascular")}
                    stackedData={[]}
                    id="hrvGraph"
                    popoverTitle="Prueba"
                    popoverBody="Cuerpo prueba"
                  />
                </Col>
                <Col xs={6} sm={6} md={4} lg={4} xl={4}>
                  <DonutChart
                    title="Salud cardiovascular (HRV)"
                    data={getCategoryCounts(
                      chartData.map((d) => d.hrvValueAvg),
                      cardiovascularHealthRanges
                    )}
                    iconString={HEART_ICON_STRING}
                  />
                </Col>
                <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                  <SummaryMessages
                    data={summaryData?.messages?.hrv}
                    isLoading={loadingBtn !== 0}
                  />
                </Col>
              </Row>
            )}

            {tab === "steps" && (
              <>
                <Row>
                  <Col sm={12} md={6}>
                    <LineChart
                      title="Pasos"
                      data={chartData.map(({ date, steps }) => ({
                        date,
                        value: steps,
                      }))}
                      refValue={0}
                      id="stepsGraph"
                      popoverTitle="Prueba"
                      popoverBody="Cuerpo prueba"
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <SummaryMessages
                      data={summaryData?.messages?.steps}
                      isLoading={loadingBtn !== 0}
                    />
                  </Col>
                </Row>
              </>
            )}

            {tab === "distance" && (
              <Row>
                <Col sm={12} md={6}>
                  <LineChart
                    title="Km recorridos"
                    data={chartData.map(({ date, distance }) => ({
                      date,
                      value: distance,
                    }))}
                    refValue={0}
                    id="distanceGraph"
                    popoverTitle="Prueba"
                    popoverBody="Cuerpo prueba"
                  />
                </Col>
                <Col sm={12} md={6}>
                  <SummaryMessages
                    data={summaryData?.messages?.distance}
                    isLoading={loadingBtn !== 0}
                  />
                </Col>
              </Row>
            )}

            {tab === "calories" && (
              <>
                <Row>
                  <Col sm={12} md={6}>
                    <BarChart
                      title="Calorías"
                      data={chartData.map(({ date, calories }) => ({
                        date,
                        value: calories,
                      }))}
                      refValue={0}
                      stackedData={[]}
                      id="caloriesGraph"
                      popoverTitle="Prueba"
                      popoverBody="Cuerpo prueba"
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <SummaryMessages
                      data={summaryData?.messages?.calories}
                      isLoading={loadingBtn !== 0}
                    />
                  </Col>
                </Row>
              </>
            )}

            {tab === "oxygen" && (
              <Row>
                <Col className="col-12">
                  <LineChart
                    title="Oxígeno"
                    data={oxygenData}
                    refValue={0}
                    stackedData={[]}
                    id="oxygenChart"
                    popoverTitle="Prueba"
                    popoverBody="Cuerpo prueba"
                  />
                </Col>
              </Row>
            )}

            {tab === "temperature" && (
              <Row>
                <Col className="col-12">
                  <LineChart
                    title="Temperatura"
                    data={temperatureData}
                    refValue={0}
                    stackedData={[]}
                    id="temperatureChart"
                    popoverTitle="Prueba"
                    popoverBody="Cuerpo prueba"
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </div>
      <AlertModal
        show={showAlertModal}
        toggle={() => setShowAlertModal(!showAlertModal)}
      >
        El rango de meses debería ser válido!
      </AlertModal>
    </React.Fragment>
  );
};

export default UserDashboard;
